import { UserType } from "@app/types/genericTables.types";
import { conditioningTablesDisableTrue } from "@app/utils/tables/conditioning.tables";
import { processingTablesDisabledTrue } from "@app/utils/tables/processing.tables";
import { yesNoTable } from "@app/utils/tables/shared.tables";
import { Box, Tab, Tabs } from "@mui/material";
import {
  useSamplingProcedMuestreo,
  useSamplingProcedure,
  useTypesUnits,
  useUser,
} from "@services/servicesGenericsTables";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import useTabs from "../../utils/useTab";
import { useParams } from "react-router-dom";
import { useSample } from "../../utils/useSample";
import useAuth from "@app/context/AuthContext";
import { pointContinuousTable } from "@app/utils/tables/pointContinuous.tables";
import { formatDateOrNow } from "@app/utils/formatDate";
import { preloadProcessings } from "../../utils/helpers";
import { setsInitialPhases } from "../../config/phases/InitialPhases";

const getDefaultValues = (
  idMuestra?: string,
  stateSample?: any,
  user?: UserType
) => {
  const { sampling, management, processing, conditioning } = stateSample;
  const userList = useUser.getState().userList;

  const idsProcessing = processing?.current?.map(
    (processing: any) => processing.processing_category?.id
  );

  const filter = processingTablesDisabledTrue
    .filter((item) => idsProcessing?.includes(item.id))
    .map((item) => item.value);

  const currentSample = useSample.getState().get(Number(idMuestra));
  const currentType = currentSample?.sample_type;
  const preloadWeek = currentSample?.week;
  const preloadYear = currentSample?.year;
  const typeUnits = useTypesUnits.getState().typesUnitsis;

  const malpmaProcedure = useSamplingProcedure
    .getState()
    .procedureList.filter(
      (item) => item.codigo === currentType?.codigo && item.codop === "REC"
    );

  const defaultTypeUnit = typeUnits.find(
    (item) => item.codigo === `${currentType?.unidades}`
  );

  const parseExternalTakesSampleEntity = JSON.parse(
    sampling.current?.externalTakesSampleEntity || "{}"
  );

  return {
    sample_registration: idMuestra,
    analysis_type: currentSample?.analysis_type,

    // sampling
    analyst: sampling.current?.analyst
      ? userList.find(
          (item) => item.id === Number(sampling.current?.analyst?.id)
        )
      : user,
    date_start_sampling: formatDateOrNow(sampling.current?.date_start_sampling),
    date_end_sampling: formatDateOrNow(sampling.current?.date_end_sampling),
    week: sampling.current?.week || preloadWeek,
    year: sampling.current?.year || preloadYear,
    amount_sample: sampling.current?.amount_sample || undefined,
    unit_types: sampling.current?.unit_types || defaultTypeUnit,
    s2_kg: sampling.current?.s2_kg || undefined,
    ll2_m: sampling.current?.ll2_m || undefined,
    m3_m2: sampling.current?.m3_m2 || undefined,
    type_sampler: sampling.current?.type_sampler,
    masl_sampler: sampling.current?.masl_sampler
      ? userList.find(
          (item) => item.id === Number(sampling.current?.masl_sampler?.id)
        )
      : user,
    proc_take_sample: sampling.current?.proc_take_sample
      ? useSamplingProcedMuestreo
          .getState()
          .samplingProcedureList.find(
            (item) => item.id === Number(sampling.current?.proc_take_sample)
          )
      : undefined,
    idSampling: sampling.id || undefined,
    conservation:
      sampling.current?.conservation !== undefined
        ? sampling.current?.conservation
          ? yesNoTable[0]
          : yesNoTable[1]
        : undefined,
    product: sampling.current?.product || undefined,
    productTimestamp: sampling.current?.productTimestamp || undefined,
    filtration:
      sampling.current?.filtration !== undefined
        ? sampling.current?.filtration
          ? yesNoTable[0]
          : yesNoTable[1]
        : undefined,
    filter: sampling.current?.filter || undefined,
    filtrationTimestamp: sampling.current?.filtrationTimestamp || undefined,
    conservationFiltrationEntity:
      sampling.current?.conservationFiltrationEntity || undefined,
    samplingType: sampling.current?.samplingType
      ? pointContinuousTable.find(
          (item) => item.id === sampling.current?.samplingType
        )
      : undefined,
    client_reference: sampling.current?.client_reference || currentSample?.client_reference || undefined,
    externalTakesSampleEntity:
      parseExternalTakesSampleEntity["externalTakesSampleEntity"] || undefined,
    external_sampler:
      parseExternalTakesSampleEntity["external_sampler"] || undefined,
    recipientPostalCode: sampling.current?.recipientPostalCode || undefined,

    //Management
    reservation:
      management.current?.reservation !== undefined
        ? management.current?.reservation
          ? yesNoTable[0]
          : yesNoTable[1]
        : yesNoTable[0],

    reception_analyst: management.current?.reception_analyst
      ? userList.find(
          (item) =>
            item.id === Number(management.current?.reception_analyst?.id)
        )
      : user,
    date_start_reception: formatDateOrNow(
      management.current?.date_start_reception
    ),
    malpma_procedure:
      management.current?.malpma_procedure || malpmaProcedure[0],
    retirement_analyst: management.current?.retirement_analyst
      ? userList.find(
          (item) =>
            item.id === Number(management.current?.retirement_analyst?.id)
        )
      : undefined,
    retirement_date: management.current?.retirement_date || undefined,
    idManagement: management.id || undefined,

    //Conditioning
    category: conditioning.current?.category
      ? conditioningTablesDisableTrue.find(
          (item) => item.id === conditioning.current.category?.id
        )?.value
      : conditioningTablesDisableTrue[4].value,
    conditioning_processing_procedure:
      conditioning.current?.conditioning_procedure || undefined,
    conditioning_equipment: conditioning.current?.equipment || undefined,
    conditioning_date_time: formatDateOrNow(conditioning.current?.date_time),
    conditioning_date_time_end: formatDateOrNow(
      conditioning.current?.date_time_end
    ),
    conditioning_analyst: conditioning.current?.analyst
      ? userList.find(
          (item) => item.id === Number(conditioning.current?.analyst?.id)
        )
      : user,
    conditioning_reagent: conditioning.current?.reagent || undefined,
    sample_for_tritium: "Alícuota con muestra sin acidificación",
    sample_amount_without_acidification:
      conditioning.current?.sample_amount_without_acidification || "0",
    idConditioning: conditioning.id || undefined,

    //processing
    processing_procedure: processing.current?.processing_procedure || undefined,
    analytical_balance: processing.current?.analytical_balance
      ? userList.find(
          (item) =>
            item.id === Number(processing.current?.analytical_balance?.id)
        )
      : [],
    processing_equipment: processing.current?.processing_equipment || undefined,
    processing_date_time: formatDateOrNow(processing.current?.date_time),
    processing_date_time_end: formatDateOrNow(
      processing.current?.date_time_end
    ),
    wet_weight: processing.current?.wet_weight || undefined,
    gross_weight: processing.current?.gross_weight || undefined,
    sample_quantity: processing.current?.sample_quantity || undefined,
    processing_reagent: processing.current?.reagent || undefined,
    volume: processing.current?.volume || undefined,
    processing_analyst: processing.current?.analyst
      ? userList.find(
          (item) => item.id === Number(processing.current?.analyst?.id)
        )
      : user,
    processing_category:
      filter.length > 0 ? filter : [processingTablesDisabledTrue[6].value],
    idProcessing: processing.id || undefined,
    ...preloadProcessings(processing?.current, user),
  };
};

const PhasesInitial = () => {
  const { tabValue, setTabValue } = useTabs();
  const form = useFormContext();
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const { idMuestra } = useParams();
  const { stateSample } = useSample();
  const { user } = useAuth();

  const CurrentComponent = setsInitialPhases[tabValue].component;
  useEffect(() => {
    if (idMuestra) {
      const defaultValues = getDefaultValues(idMuestra, stateSample, user);
      form.reset(defaultValues);
    }
  }, [idMuestra]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Tabs
        value={tabValue}
        onChange={handleChange}
        aria-label="Fields initial tabs"
      >
        {setsInitialPhases.map((set, index) => (
          <Tab key={index} label={set.label} />
        ))}
      </Tabs>
      <Box mt={2}>
        <CurrentComponent key={stateSample} />
      </Box>
    </>
  );
};

export default PhasesInitial;
